import { useMemo } from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthProps {
  password: string;
  /** Color de los iconos / textos cumplidos. Por defecto verde. */
  accentClass?: string;
}

export function usePasswordValidation(password: string) {
  const requirements = useMemo(
    () => ({
      minLength: password.length >= 6,
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    }),
    [password]
  );

  const isValid =
    requirements.minLength &&
    requirements.hasUpperCase &&
    requirements.hasNumber &&
    requirements.hasSymbol;

  const strength =
    (requirements.minLength ? 25 : 0) +
    (requirements.hasUpperCase ? 25 : 0) +
    (requirements.hasNumber ? 25 : 0) +
    (requirements.hasSymbol ? 25 : 0);

  return { requirements, isValid, strength };
}

const getStrengthColor = (strength: number) => {
  if (strength < 34) return 'bg-red-500';
  if (strength < 67) return 'bg-yellow-500';
  return 'bg-green-600';
};

export function PasswordStrength({ password, accentClass = 'text-green-600' }: PasswordStrengthProps) {
  const { requirements, strength } = usePasswordValidation(password);
  if (!password) return null;

  const items: Array<{ ok: boolean; label: string }> = [
    { ok: requirements.minLength, label: 'Mínimo 6 caracteres' },
    { ok: requirements.hasUpperCase, label: 'Una letra mayúscula' },
    { ok: requirements.hasNumber, label: 'Un número' },
    { ok: requirements.hasSymbol, label: 'Un símbolo (!@#$%^&*)' },
  ];

  return (
    <div className="mt-3 space-y-2">
      {items.map((item) => (
        <div key={item.label} className="flex items-center gap-2 text-sm">
          {item.ok ? (
            <Check className={`h-4 w-4 ${accentClass}`} />
          ) : (
            <X className="h-4 w-4 text-muted-foreground" />
          )}
          <span className={item.ok ? `${accentClass} font-medium` : 'text-muted-foreground'}>
            {item.label}
          </span>
        </div>
      ))}
      <div className="mt-3">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-muted-foreground">Fortaleza</span>
          <span className="text-xs font-medium">{Math.round(strength)}%</span>
        </div>
        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${getStrengthColor(strength)}`}
            style={{ width: `${strength}%` }}
          />
        </div>
      </div>
    </div>
  );
}
